import { createContext, useRef, useState } from "react";
import EditScheduledUpdateModal, {
  ScheduledUpdateValues,
} from "./EditScheduledUpdateModal";
import useScheduledUpdates from "./hooks/useScheduledUpdates";
import { SectionProps } from "../sections/sectionsComponents";
import {
  KYDEventScheduledUpdateSummary,
  KYDPerformerDisplay,
} from "@common/interfaces/KYDEvent";
import { BodyDoc } from "@common/interfaces/KYDCampaign";

export const ScheduledUpdatesContext = createContext({
  open: (
    options: {
      scheduledUpdate?: KYDEventScheduledUpdateSummary;
      field?: "name" | "description" | "poster" | "performers" | "date";
      afterSave?: () => Promise<void>;
    } = {}
  ) => {},
  loading: {
    scheduledUpdate: false,
  },
  close: () => {},
});

export const ScheduledUpdatesProvider = ({
  data,
  refetchEvent,
  children,
}: SectionProps<{
  id: string;
  name: string;
  description: string;
  poster: string;
  performers: KYDPerformerDisplay[];
  scheduled_updates: KYDEventScheduledUpdateSummary[];
  timezone: string;
  link: string;
  short_link: string;
}> & {
  children: React.ReactNode;
}) => {
  const {
    actions,
    state: {
      editingScheduledUpdate,
      editingField,
      isEditingModalOpen,
      loading,
      setEditingScheduledUpdate,
      setEditingField,
      setIsEditingModalOpen,
    },
  } = useScheduledUpdates();

  const afterSaveCallback = useRef<() => Promise<void> | null>(null);

  const open = (
    options: {
      scheduledUpdate?: KYDEventScheduledUpdateSummary;
      field?:
        | "name"
        | "description"
        | "description_rich"
        | "poster"
        | "performers"
        | "date";
      afterSave?: () => Promise<void>;
    } = {}
  ) => {
    const { scheduledUpdate, field } = options;

    setIsEditingModalOpen(true);
    afterSaveCallback.current = options.afterSave;

    if (scheduledUpdate) {
      console.log("scheduledUpdate", scheduledUpdate);
      setEditingScheduledUpdate(scheduledUpdate);
      setEditingField(field);
      return;
    }
  };

  const close = () => {
    setIsEditingModalOpen(false);
    setEditingScheduledUpdate(null);
    setEditingField(null);
    afterSaveCallback.current = null;
  };

  const onCreate = async (
    values: ScheduledUpdateValues,
    options: { justPerformers?: boolean }
  ) => {
    await actions.onCreateScheduledUpdate(data.id, values, {
      ...options,
      onSuccess: async () => {
        await refetchEvent({ silent: true });
        setIsEditingModalOpen(false);
        setEditingScheduledUpdate(null);
        setEditingField(null);
        if (afterSaveCallback.current) {
          await afterSaveCallback.current();
        }
      },
    });
  };

  const onEdit = async (values: ScheduledUpdateValues) => {
    await actions.onUpdateScheduledUpdate(data.id, values, {
      onSuccess: async () => {
        await refetchEvent({ silent: true });
        setIsEditingModalOpen(false);
        setEditingScheduledUpdate(null);
        setEditingField(null);

        if (afterSaveCallback.current) {
          await afterSaveCallback.current();
        }
      },
    });
    setIsEditingModalOpen(false);
    setEditingScheduledUpdate(null);
    setEditingField(null);
  };

  return (
    <ScheduledUpdatesContext.Provider value={{ loading, open, close }}>
      <EditScheduledUpdateModal
        data={data}
        isLoading={loading.scheduledUpdate}
        isOpen={isEditingModalOpen}
        onClose={() => {
          setIsEditingModalOpen(false);
          setEditingScheduledUpdate(null);
          setEditingField(null);
        }}
        onSubmit={editingScheduledUpdate ? onEdit : onCreate}
        scheduledUpdate={editingScheduledUpdate}
        field={editingField}
      />
      {children}
    </ScheduledUpdatesContext.Provider>
  );
};

export const withScheduledUpdates = (Component: React.ComponentType<any>) => {
  return (
    props: SectionProps<{
      scheduled_updates: KYDEventScheduledUpdateSummary[];
      id: string;
      timezone: string;
      link: string;
      short_link: string;
      name: string;
      description: string;
      description_rich?: BodyDoc;
      poster: string;
      performers: KYDPerformerDisplay[];
    }>
  ) => {
    return (
      <ScheduledUpdatesProvider {...props}>
        <Component {...props} />
      </ScheduledUpdatesProvider>
    );
  };
};
